import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatMoney, formatPercent } from "@bookboost/shared/format";

interface LeaderboardResponse {
  year: number;
  baseCurrency: string;
  fxWarnings: string[];
  team: {
    earned: number;
    paid: number;
    pending: number;
    aging: number;
    deals: number;
    reversals: number;
    commissionCount: number;
    reversalRate: number;
  };
  rows: Array<{
    id: string;
    email: string;
    full_name: string | null;
    stats: {
      earned: number;
      cash: number;
      equity: number;
      paid: number;
      pending: number;
      pendingCount: number;
      aging: number;
      agingCount: number;
      dealsClosed: number;
      commissionCount: number;
      reversals: number;
    };
    structure: {
      structure_type: "flat" | "split_equity";
      base_commission_rate: number;
      equity_annual_cap: number | null;
      cap_currency: string | null;
      effective_from: string | null;
    } | null;
    cap: { consumed: number; cap: number; pct: number; capReached: boolean } | null;
  }>;
}

const leaderboardSearch = z.object({ year: z.coerce.number().optional() });

export const Route = createFileRoute("/admin/leaderboard")({
  validateSearch: leaderboardSearch,
  component: LeaderboardPage,
});

function LeaderboardPage() {
  const { year } = Route.useSearch();
  const navigate = Route.useNavigate();
  const thisYear = new Date().getUTCFullYear();
  const years = [thisYear, thisYear - 1, thisYear - 2];

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "leaderboard", { year }],
    queryFn: () => api.get<LeaderboardResponse>("/api/admin/leaderboard", { searchParams: { year } }),
  });

  if (isLoading) return <p className="lede">Loading leaderboard…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Leaderboard unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  const base = data.baseCurrency;

  return (
    <>
      <h1>Leaderboard · {data.year}</h1>

      <form
        className="inline filters"
        style={{ gap: 12, marginBottom: 16 }}
        onSubmit={(e) => e.preventDefault()}
      >
        <label className="muted" htmlFor="year-f">
          Year
        </label>
        <select
          id="year-f"
          value={String(data.year)}
          onChange={(e) => navigate({ search: { year: Number(e.target.value) } })}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          All amounts converted to {base} at current FX.
        </p>
      </form>

      {data.fxWarnings.length > 0 && (
        <Panel variant="warn">
          <strong>⚠ FX rates missing for:</strong> {data.fxWarnings.join(", ")}. Totals below exclude commissions in
          those currencies — <a href="/admin/fx">add the rate</a>.
        </Panel>
      )}

      <div className="grid cols-4">
        <Metric
          label={`Team earned ${data.year}`}
          value={formatMoney(data.team.earned, base)}
          sub={`${data.team.commissionCount} commission${data.team.commissionCount === 1 ? "" : "s"} written`}
          tone="accent"
          hint="Sum of all commissions earned this year across every rep, FX-converted to base. Includes reversal rows (offsetting)."
        />
        <Metric
          label={`Team paid ${data.year}`}
          value={formatMoney(data.team.paid, base)}
          sub={data.team.earned > 0 ? `${((data.team.paid / data.team.earned) * 100).toFixed(0)}% of earned` : "—"}
          hint="Earned commissions that finance has marked paid."
        />
        <Metric
          label="Outstanding pending"
          value={formatMoney(data.team.pending, base)}
          sub={data.team.aging > 0 ? `${formatMoney(data.team.aging, base)} > 30d old` : "all current"}
          tone={data.team.aging > 0 ? "err" : "default"}
          hint="All-time earned but unpaid commissions. Aging highlights anything sitting > 30 days."
        />
        <Metric
          label={`Deals closed ${data.year}`}
          value={String(data.team.deals)}
          sub={
            data.team.reversals > 0
              ? `${data.team.reversals} reversal${data.team.reversals === 1 ? "" : "s"} (${data.team.reversalRate.toFixed(1)}%)`
              : "no reversals"
          }
          tone={data.team.reversalRate > 5 ? "err" : "default"}
          hint="Count of HubSpot deals with closed_won_at in this year, attributed to a rep."
        />
      </div>

      <Panel padding="tight">
        {data.rows.length === 0 ? (
          <EmptyState icon="generic" title="No active reps" body="Invite or activate reps to see the leaderboard." />
        ) : (
          <DataTable stickyFirstColumn>
            <table className="table-cards leaderboard">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Rep</th>
                  <th>Deals</th>
                  <th>Earned YTD</th>
                  <th>Paid YTD</th>
                  <th>Pending</th>
                  <th>Aging</th>
                  <th>Equity cap</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td data-label="#" className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {idx + 1}
                    </td>
                    <td data-label="Rep">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong>{r.full_name ?? r.email}</strong>
                        {r.full_name && (
                          <span className="muted" style={{ fontSize: 11, fontFamily: "var(--font-mono)" }}>
                            {r.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Deals">{r.stats.dealsClosed}</td>
                    <td data-label="Earned YTD">
                      <strong>{formatMoney(r.stats.earned, base)}</strong>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {formatMoney(r.stats.cash, base)} cash · {formatMoney(r.stats.equity, base)} equity
                      </div>
                    </td>
                    <td data-label="Paid YTD">{formatMoney(r.stats.paid, base)}</td>
                    <td data-label="Pending">
                      {formatMoney(r.stats.pending, base)}
                      <div className="muted" style={{ fontSize: 11 }}>
                        {r.stats.pendingCount} row{r.stats.pendingCount === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td data-label="Aging" className={r.stats.aging > 0 ? "err" : "muted"}>
                      {r.stats.aging > 0 ? formatMoney(r.stats.aging, base) : "—"}
                      {r.stats.aging > 0 && (
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>
                          {r.stats.agingCount} {">"} 30d
                        </div>
                      )}
                    </td>
                    <td data-label="Equity cap">
                      {r.cap ? (
                        <CapBar consumed={r.cap.consumed} cap={r.cap.cap} pct={r.cap.pct} capReached={r.cap.capReached} base={base} />
                      ) : r.structure?.structure_type === "split_equity" ? (
                        <span className="muted">no tracking</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>
    </>
  );
}

function CapBar({
  consumed,
  cap,
  pct,
  capReached,
  base,
}: {
  consumed: number;
  cap: number;
  pct: number;
  capReached: boolean;
  base: string;
}) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
        <span>{formatMoney(consumed, base)}</span>
        <span className="muted">/ {formatMoney(cap, base)}</span>
      </div>
      <div
        style={{
          height: 4,
          marginTop: 4,
          background: "var(--gray100)",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            background: capReached ? "var(--err)" : "var(--primary600)",
            height: "100%",
            transition: "width 200ms ease",
          }}
        />
      </div>
      <div style={{ fontSize: 10, marginTop: 2, color: capReached ? "var(--err)" : "var(--muted)" }}>
        {capReached ? "cap reached" : `${formatPercent(pct / 100)} of cap`}
      </div>
    </div>
  );
}
